import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FC } from "react";
import { useTranslation } from "react-i18next";

const bodySx = {
  color: "text.secondary",
  lineHeight: 1.7,
} as const;

const sectionTitleSx = {
  fontWeight: 700,
  letterSpacing: "-0.02em",
  mb: 1.5,
} as const;

export const ExplanationOpensourceLooping: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const considerations = [
    t(
      "Open-source looping is not a medical device and is not approved by regulatory authorities. It requires a thorough understanding of diabetes management, technical setup, and ongoing monitoring. Users take full responsibility for their system configuration and use."
    ),
    t(
      "We strongly recommend discussing open-source looping with your healthcare provider before starting. Our team provides educational support and guidance, but we do not replace your medical care team."
    ),
  ];

  return (
    <Box sx={{ backgroundColor: "background.paper", minHeight: "100vh" }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          pt: { xs: 10, md: 14 },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 680 }}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.14em",
                color: "primary.main",
                mb: 1.5,
                display: "block",
              }}
            >
              {t("What is Open Source Looping?")}
            </Typography>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.03em",
                lineHeight: 1.05,
                fontSize: smDown ? "2.75rem" : "4rem",
              }}
            >
              {t("About Open-Source Looping")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 2,
                maxWidth: 600,
                fontSize: smDown ? "1rem" : "1.125rem",
                lineHeight: 1.6,
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              {t(
                "Open-source diabetes management through automated insulin delivery systems."
              )}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ pt: { xs: 3, md: 4 }, pb: { xs: 6, md: 8 } }}
      >
        <Box sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
          <Typography component="h2" variant="h3" sx={sectionTitleSx}>
            {t("Loop algorithm")}
          </Typography>
          <Stack spacing={2.5}>
            <Typography variant="body1" sx={bodySx}>
              {t(
                "Open-source looping is an innovative approach to diabetes management that uses open-source software to create an automated insulin delivery system, often called an 'artificial pancreas.' This technology combines continuous glucose monitoring (CGM) with insulin pump therapy, creating a closed-loop system that can automatically adjust insulin delivery based on real-time glucose readings."
              )}
            </Typography>
            <Typography variant="body1" sx={bodySx}>
              {t(
                "The system works by continuously monitoring your blood glucose levels through a sensor, typically worn on your arm or abdomen. This data is transmitted to a smartphone running open-source software like AndroidAPS, which calculates the optimal insulin dose based on your glucose trends, meal intake, and personal settings."
              )}
            </Typography>
            <Typography variant="body1" sx={bodySx}>
              {t(
                "The software then communicates with your insulin pump to deliver the calculated amount of insulin automatically. This creates a feedback loop that continuously adjusts insulin delivery to keep your glucose levels within your target range, reducing the need for constant manual intervention."
              )}
            </Typography>
          </Stack>
        </Box>
        <Box
          component="img"
          src="assets/aboutus/General Overview.png"
          alt="Open Source Looping System Overview"
          sx={{ width: "100%", maxWidth: 900, display: "block" }}
        />
      </Container>

      <Box
        sx={{
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(99, 102, 241, 0.08)"
              : "#F7F9FC",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
            <Typography component="h2" variant="h3" sx={sectionTitleSx}>
              {t("Devices")}
            </Typography>
            <Typography variant="body1" sx={bodySx}>
              {t(
                "There are several devices that can be connected to the loop. Typically, a continuous glucose monitor (CGM) continuously measures glucose values and sends them to a smartphone. Open-source software such as AndroidAPS, OpenAPS or iLoop uses these readings together with your personal settings to calculate insulin needs. Compatible insulin pumps receive these commands and deliver precisely the required amount of insulin. Depending on your setup, smartwatches and other accessories can provide convenient alerts and controls."
              )}
            </Typography>
          </Box>
          <Box
            component="img"
            src="assets/aboutus/Devices.png"
            alt="Devices that can be connected to the loop"
            sx={{
              width: "100%",
              maxWidth: 520,
              display: "block",
              bgcolor: "transparent",
              mixBlendMode: theme.palette.mode === "dark" ? "normal" : "darken",
            }}
          />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
          <Typography component="h2" variant="h3" sx={sectionTitleSx}>
            {t("Our support")}
          </Typography>
          <Typography variant="body1" sx={bodySx}>
            {t(
              "The source-code is available for everyone.  We believe that also the implementation of the above technologies should be available for everyone and that nobody should feel held back by a lack of IT knowledge. Through open-source looping and services like Nightscout, data can be stored securely in the cloud, shared with caregivers, and analyzed to improve therapy. Our platform focuses on making the setup, hosting, and monitoring straightforward so you can concentrate on living your life while benefiting from modern diabetes technology."
            )}
          </Typography>
        </Box>
        <Box
          component="img"
          src="assets/aboutus/Overview OSC.png"
          alt="Cloud services via open-source looping"
          sx={{
            width: "100%",
            maxWidth: 900,
            display: "block",
            mb: { xs: 5, md: 7 },
          }}
        />

        <Box sx={{ maxWidth: 720 }}>
          <Typography component="h2" variant="h3" sx={sectionTitleSx}>
            {t("Important Considerations")}
          </Typography>
          <Stack spacing={3}>
            {considerations.map((point) => (
              <Box
                key={point}
                sx={{
                  borderLeft: 3,
                  borderColor: "primary.main",
                  pl: 2.5,
                  py: 0.5,
                }}
              >
                <Typography variant="body1" sx={bodySx}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};
